<template>
  <div class="mdash-page">
    <!-- page header rail. this was the one landing page with no header at
         all, so its controls floated above the grid with nothing to anchor
         them and the page never said its own name. same construction as the
         ops_desk rail (accent edge, gradient band, title left / actions
         right). the range group and Customize move INTO it rather than
         sitting in their own bar above the widgets. -->
    <div class="mdash-rail">
      <h3 class="mdash-rail-title"><span class="mdash-rail-title-text">Dashboard</span> <FiFavStar /></h3>
      <div v-if="!editing" class="mdash-rail-actions">
        <div class="mdash-range-group" role="group" aria-label="Stats date range">
          <button
            v-for="option in dateRangeOptions"
            :key="option"
            type="button"
            class="mdash-range-btn"
            :class="{ 'mdash-range-btn--active': days_range === option }"
            :disabled="page_loading"
            @click="set_days_range(option)"
          >{{ option }}d</button>
        </div>
        <b-button
          size="sm"
          variant="outline-secondary"
          class="mdash-customize-btn"
          title="Customize dashboard"
          aria-label="Customize dashboard"
          @click="start_editing"
        >
          <i class="fa fa-sliders" aria-hidden="true"></i>
        </b-button>
      </div>
    </div>

    <div v-if="editing" class="mdash-edit-bar">
      <div class="mdash-edit-bar-hint">
        <i class="fa fa-arrows" aria-hidden="true"></i>
        Drag to reorder, use the eye to show/hide, or add a widget from the catalog.
      </div>
      <div class="mdash-edit-bar-actions">
        <b-button size="sm" variant="outline-secondary" @click="show_add_modal = true">
          <i class="fa fa-plus" aria-hidden="true"></i> Add Widget
        </b-button>
        <b-button size="sm" variant="outline-secondary" @click="reset_to_defaults">Reset to Defaults</b-button>
        <b-button size="sm" variant="outline-secondary" :disabled="saving" @click="cancel_editing">Cancel</b-button>
        <b-button size="sm" variant="primary" :disabled="saving" @click="save_editing">
          <b-spinner v-if="saving" small></b-spinner> Save Layout
        </b-button>
      </div>
    </div>

    <div v-if="page_loading" class="text-center" style="margin-top: 60px">
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>

    <template v-else>
      <!-- pinned: always full-width, always first, not part of the
           customizable layout (no drag handle, no hide toggle, excluded
           from layout/catalog via WIDGET_CATALOG's pinned flag). -->
      <div
        class="mdash-card mdash-card--full"
        :style="{ '--mdash-accent': categoryColor('company_overview') }"
      >
        <div class="mdash-card-header">
          <span class="mdash-card-title">
            <i :class="'fa ' + widgetDef('company_overview').icon" aria-hidden="true"></i>
            {{ widgetDef('company_overview').name }}
            <span v-if="summary.tenant_name" class="mdash-card-subtitle">{{ summary.tenant_name }}</span>
          </span>
          <router-link
            v-if="detailRoute('company_overview')"
            :to="detailRoute('company_overview')"
            class="mdash-see-more"
            :aria-label="'See more: ' + widgetDef('company_overview').name"
          >
            See more <i class="fa fa-angle-right" aria-hidden="true"></i>
          </router-link>
        </div>
        <div class="mdash-card-body">
          <component :is="widgetComponent('company_overview')" v-bind="widgetProps('company_overview')" />
        </div>
      </div>

      <div v-if="!layout.length" class="dw-empty" style="margin-top: 20px">
        Your dashboard has no widgets yet.
        <a href="#" @click.prevent="reset_to_defaults">Restore the default set</a>.
      </div>

      <draggable
        v-else-if="editing"
        v-model="layout"
        tag="div"
        class="mdash-grid"
        handle=".mdash-card-header"
        ghost-class="mdash-ghost"
      >
        <div
          v-for="entry in layout"
          :key="entry.id"
          class="mdash-card"
          :class="[widthClass(entry.id), { 'mdash-card--hidden': !entry.visible }]"
          :style="{ '--mdash-accent': categoryColor(entry.id) }"
        >
          <div class="mdash-card-header mdash-card-header--draggable">
            <span class="mdash-card-title">
              <i class="fa fa-bars mdash-drag-handle" aria-hidden="true" title="Drag anywhere in the header to reorder"></i>
              <i :class="'fa ' + widgetDef(entry.id).icon" aria-hidden="true"></i>
              {{ widgetDef(entry.id).name }}
            </span>
            <span v-if="isDateScoped(entry.id)" class="mdash-range-badge">{{ days_range }}d</span>
            <button type="button" class="mdash-icon-btn" :title="entry.visible ? 'Hide widget' : 'Show widget'" @click="toggle_visible(entry.id)">
              <i class="fa" :class="entry.visible ? 'fa-eye' : 'fa-eye-slash'" aria-hidden="true"></i>
            </button>
          </div>
          <div class="mdash-card-body">
            <div v-if="!entry.visible" class="dw-empty">Hidden. Click the eye to show it on your dashboard.</div>
            <component :is="widgetComponent(entry.id)" v-else v-bind="widgetProps(entry.id)" />
          </div>
        </div>
      </draggable>

      <div v-else class="mdash-grid">
        <div
          v-for="entry in visibleWidgets"
          :key="entry.id"
          class="mdash-card"
          :class="widthClass(entry.id)"
          :style="{ '--mdash-accent': categoryColor(entry.id) }"
        >
          <div class="mdash-card-header">
            <span class="mdash-card-title">
              <i :class="'fa ' + widgetDef(entry.id).icon" aria-hidden="true"></i>
              {{ widgetDef(entry.id).name }}
            </span>
            <span v-if="isDateScoped(entry.id)" class="mdash-range-badge">{{ days_range }}d</span>
            <router-link
              v-if="detailRoute(entry.id)"
              :to="detailRoute(entry.id)"
              class="mdash-see-more"
              :aria-label="'See more: ' + widgetDef(entry.id).name"
            >
              See more <i class="fa fa-angle-right" aria-hidden="true"></i>
            </router-link>
          </div>
          <div class="mdash-card-body">
            <component :is="widgetComponent(entry.id)" v-bind="widgetProps(entry.id)" />
          </div>
        </div>
      </div>
    </template>

    <b-modal v-model="show_add_modal" title="Add a Widget" hide-footer size="lg">
      <div v-if="!addableGroups.length" class="dw-empty">Every available widget is already on your dashboard.</div>
      <div v-for="group in addableGroups" :key="group.category" class="mdash-add-group">
        <div class="mdash-add-group-title">{{ group.category }}</div>
        <div class="mdash-add-list">
          <div v-for="widget in group.widgets" :key="widget.id" class="mdash-add-row">
            <div class="mdash-add-row-icon"><i :class="'fa ' + widget.icon" aria-hidden="true"></i></div>
            <div class="mdash-add-row-text">
              <div class="mdash-add-row-title">{{ widget.name }}</div>
              <div class="mdash-add-row-desc">{{ widget.description }}</div>
            </div>
            <b-button size="sm" variant="outline-primary" @click="add_widget(widget.id)">Add</b-button>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import draggable from 'vuedraggable'
import { get_tenant_dashboard, get_dashboard_config, update_dashboard_config, update_dashboard_range, get_alerts } from '../networking/apis'
import { getApplicableCatalog, getDefaultLayout, getWidgetDefinition, mergeNewDefaults } from '../utils/dashboard_widgets'
import { vsmRouteName } from '../utils/vsm_access'

import widget_company_overview from './dashboard_widgets/widget_company_overview'
import widget_at_a_glance from './dashboard_widgets/widget_at_a_glance'
import widget_quick_links from './dashboard_widgets/widget_quick_links'
import widget_notifications from './dashboard_widgets/widget_notifications'
import widget_recent_alerts from './dashboard_widgets/widget_recent_alerts'
import widget_alerts_by_type from './dashboard_widgets/widget_alerts_by_type'
import widget_delivery_status from './dashboard_widgets/widget_delivery_status'
import widget_recent_delivery_errors from './dashboard_widgets/widget_recent_delivery_errors'
import widget_actuals_summary from './dashboard_widgets/widget_actuals_summary'
import widget_actuals_by_status from './dashboard_widgets/widget_actuals_by_status'
import widget_driver_on_time from './dashboard_widgets/widget_driver_on_time'
import widget_alerts_summary from './dashboard_widgets/widget_alerts_summary'
import widget_sms_stats from './dashboard_widgets/widget_sms_stats'
import widget_vehicle_maintenance from './dashboard_widgets/widget_vehicle_maintenance'
import widget_ops_events from './dashboard_widgets/widget_ops_events'
import widget_coaching_portal from './dashboard_widgets/widget_coaching_portal'
import widget_driver_manager from './dashboard_widgets/widget_driver_manager'
import widget_support_tickets from './dashboard_widgets/widget_support_tickets'

// kept in step with DASHBOARD_DAYS_RANGE_OPTIONS in fleet-int-api's
// fleet_service.py, which validates the range before storing it on the user's
// profile -- and drops a stored value it no longer offers, so anyone left on
// a retired window (1d, 90d) reads back as unset and lands on the default.
const DATE_RANGE_OPTIONS = [3, 7, 30]
const DEFAULT_DATE_RANGE = 7

// widgets whose figures are scoped to the days_range window, so their card
// header carries a subtle "30d"-style badge showing the window. All alert
// stats use this shared window now, including the "recent" feeds, as does At
// a Glance (which windows server-side, and whose "out now" figure stays
// real-time inside an otherwise windowed card); only
// notifications/support_tickets (not alert data) keep their own fixed
// lookback and stay off this list.
const DATE_SCOPED_WIDGET_IDS = [
  'actuals_summary', 'actuals_by_status', 'alerts_summary', 'sms_stats', 'alerts_by_type', 'delivery_status',
  'recent_alerts', 'recent_delivery_errors', 'driver_on_time', 'at_a_glance',
]

// per-category left-rail accent, reusing the exact section accent hexes
// from site_map.vue (sm-section-card--core/bookings/alerts/vsm/reports)
// so the dashboard's category colors read as the same system, not a
// separate palette. Overview maps to site map's "core" section (dashboard,
// notifications, settings, ...); Fleet/Drivers map to vsm/reports since
// those categories are single-widget today (Vehicle Status Manager,
// Driver Manager) and mirror those sections directly.
const CATEGORY_COLORS = {
  'Overview': '#3a72a8',
  'Bookings & Actuals': '#6a4fa8',
  'Alerts': '#c86e18',
  'Fleet': '#4a6878',
  'Drivers': '#6a5290',
}
const DEFAULT_CATEGORY_COLOR = '#4682b4'

const WIDGET_COMPONENTS = {
  company_overview: widget_company_overview,
  at_a_glance: widget_at_a_glance,
  quick_links: widget_quick_links,
  notifications: widget_notifications,
  recent_alerts: widget_recent_alerts,
  alerts_by_type: widget_alerts_by_type,
  delivery_status: widget_delivery_status,
  recent_delivery_errors: widget_recent_delivery_errors,
  actuals_summary: widget_actuals_summary,
  actuals_by_status: widget_actuals_by_status,
  driver_on_time: widget_driver_on_time,
  alerts_summary: widget_alerts_summary,
  sms_stats: widget_sms_stats,
  vehicle_maintenance: widget_vehicle_maintenance,
  ops_events: widget_ops_events,
  coaching_portal: widget_coaching_portal,
  driver_scorecard: widget_driver_manager,
  support_tickets: widget_support_tickets,
}

export default {
  name: 'main_dashboard',
  components: { draggable },
  data() {
    return {
      page_loading: true,
      stats_loading: true,
      alerts_loading: true,
      days_range: DEFAULT_DATE_RANGE,
      summary: {},
      // raw alert records for the current days_range, shared by every
      // alert-detail widget (Alerts Summary, Alerts By Type, Delivery
      // Status, Recent Alerts, Recent Delivery Errors) so they cost one
      // get_alerts call between them instead of one each.
      alerts_detail: [],
      // the API caps the response at the N most recent alerts (see
      // summary.truncated) -- the aggregate widgets footnote that their
      // counts only cover the capped slice.
      alerts_truncated: false,
      alerts_results_limit: 0,
      layout: [],
      editing: false,
      editing_snapshot: null,
      saving: false,
      show_add_modal: false,
    }
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
      viewAs: state => state.profile.view_as,
    }),
    dateRangeOptions() {
      return DATE_RANGE_OPTIONS
    },
    tenant_type() {
      return (this.user.types && this.user.tenant && this.user.types[this.user.tenant]) || null
    },
    isCmTenant() {
      return this.tenant_type === 'CM'
    },
    isTbn() {
      return this.tenant_type === 'TBN'
    },
    features() {
      return (this.user.features && this.user.tenant && this.user.features[this.user.tenant]) || {}
    },
    catalog() {
      return getApplicableCatalog(this.user)
    },
    catalogById() {
      const map = {}
      this.catalog.forEach(widget => { map[widget.id] = widget })
      return map
    },
    visibleWidgets() {
      return this.layout.filter(entry => entry.visible)
    },
    addableGroups() {
      const existing = new Set(this.layout.map(entry => entry.id))
      const byCategory = {}
      this.catalog.filter(widget => !existing.has(widget.id)).forEach(widget => {
        (byCategory[widget.category] = byCategory[widget.category] || []).push(widget)
      })
      return Object.keys(byCategory).map(category => ({ category, widgets: byCategory[category] }))
    },
  },
  async created() {
    await this.load_all()
  },
  methods: {
    widgetDef(id) {
      return this.catalogById[id] || getWidgetDefinition(id) || { name: id, icon: 'fa-square-o' }
    },
    isDateScoped(id) {
      return DATE_SCOPED_WIDGET_IDS.includes(id)
    },
    categoryColor(id) {
      const category = this.widgetDef(id).category
      return CATEGORY_COLORS[category] || DEFAULT_CATEGORY_COLOR
    },
    widgetComponent(id) {
      return WIDGET_COMPONENTS[id]
    },
    // the "See more" header link's destination: each widget's full detail
    // page, or null for widgets with no single page behind them. Shown only
    // in view mode -- the edit-mode header belongs to the drag/hide controls.
    detailRoute(id) {
      switch (id) {
        case 'at_a_glance':
          // the actuals list -- trip by trip, the detail behind every figure
          // on this card. no date query: that page owns its own range picker
          // and only reads booking_id off the query string.
          return { name: this.isTbn ? 'actuals_tbn' : 'actuals' }
        case 'quick_links':
          // the site map is hidden from drivers (nav/registry.js, navbar.vue) and
          // the router bounces them off it, so the widget keeps its own shortcuts
          // but loses the "see more" that would dead-end them
          return this.user.role === 'driver' ? null : { name: 'site_map' }
        case 'notifications':
          return { name: 'notifications_list' }
        case 'actuals_summary':
        case 'actuals_by_status':
          // FastTrak has no Actuals Stats page (see booking_pages.js), so
          // its actuals widgets link to the actuals list instead.
          if (this.tenant_type === 'FASTTRAK') return { name: 'actuals_fasttrak' }
          return { name: 'actuals_stats', query: { source: this.isTbn ? 'tbn' : 'cm' } }
        case 'alerts_summary':
        case 'alerts_by_type':
          return { name: 'alerts_dashboard' }
        case 'recent_alerts':
          return { name: 'alert_list' }
        case 'delivery_status':
        case 'recent_delivery_errors':
          return { name: 'delivery_failures' }
        case 'sms_stats':
          // SMS Usage is an internal-admin page (site_map_pages.js
          // coreSection) -- tenant admins see the widget but have no
          // detail page to land on.
          return this.user.role === 'admin' && this.viewAs !== 'non-admin'
            ? { name: 'sms_usage_dashboard' }
            : null
        case 'vehicle_maintenance':
          return {
            name: this.tenant_type === 'FASTTRAK'
              ? 'vehicle_status_dvir_fasttrak'
              : vsmRouteName(this.tenant_type, this.features),
          }
        case 'driver_on_time':
          return {
            name: this.isCmTenant ? 'actuals_on_time_report' : 'actuals_on_time_report_tbn',
            query: {
              fromDate: this.range_start_date().format('YYYY-MM-DD'),
              toDate: moment().format('YYYY-MM-DD'),
            },
          }
        case 'ops_events':
          // the event log itself -- the widget's chips list a status queue
          // in place and its rows deep-link into one event, so the header
          // link is the unfiltered list
          return { name: 'ops_desk' }
        case 'coaching_portal':
          // the card's chips filter in place and its rows deep-link into a
          // single ticket, so the header link is the unfiltered list
          return { name: 'coaching_portal' }
        case 'driver_scorecard':
          // FI-2064: this widget links to the Driver Manager Dashboard (rebranded
          // from Driver Scorecard); Individual Driver/Compare are now their own
          // pages reachable from there.
          return { name: 'driver_manager_dashboard' }
        case 'support_tickets':
          return { name: 'help_center', query: { tab: 'support' } }
        default:
          return null
      }
    },
    widthClass(id) {
      const width = (this.widgetDef(id) || {}).width
      return width === 'full' ? 'mdash-card--full' : ''
    },
    widgetProps(id) {
      switch (id) {
        case 'company_overview':
          // account counts aren't windowed by days_range, so this only
          // shows a loading state on first load, not on range changes
          // (unlike the actuals/alerts/sms widgets below).
          return { summary: this.summary, loading: this.page_loading }
        case 'at_a_glance':
          // every figure comes from one /glance call the widget makes itself,
          // windowed server-side by the range it is handed here
          return { tenant: this.user.tenant, isTbn: this.isTbn, daysRange: this.days_range }
        case 'quick_links':
          return { user: this.user, viewAs: this.viewAs }
        case 'notifications':
          return { tenant: this.user.tenant, isTbn: this.isTbn }
        case 'recent_alerts':
          return { alerts: this.alerts_detail, loading: this.alerts_loading, daysRange: this.days_range }
        case 'alerts_by_type':
          return { alerts: this.alerts_detail, loading: this.alerts_loading, truncated: this.alerts_truncated, resultsLimit: this.alerts_results_limit }
        case 'delivery_status':
          return { alerts: this.alerts_detail, loading: this.alerts_loading, truncated: this.alerts_truncated, resultsLimit: this.alerts_results_limit }
        case 'recent_delivery_errors':
          return { alerts: this.alerts_detail, loading: this.alerts_loading, daysRange: this.days_range }
        case 'actuals_summary':
          return { totals: this.summary.actuals_totals, loading: this.stats_loading }
        case 'actuals_by_status':
          return { summary: this.summary, loading: this.stats_loading, daysRange: this.days_range }
        case 'driver_on_time':
          return { tenant: this.user.tenant, isCmTenant: this.isCmTenant, daysRange: this.days_range }
        case 'alerts_summary':
          return { alerts: this.alerts_detail, loading: this.alerts_loading, truncated: this.alerts_truncated, resultsLimit: this.alerts_results_limit }
        case 'sms_stats':
          return { totals: this.summary.alert_totals, loading: this.stats_loading }
        case 'vehicle_maintenance':
          return { tenant: this.user.tenant }
        case 'ops_events':
          return { tenant: this.user.tenant }
        case 'coaching_portal':
          return { tenant: this.user.tenant }
        case 'driver_scorecard':
          return { tenant: this.user.tenant, isCmTenant: this.isCmTenant }
        case 'support_tickets':
          return { tenant: this.user.tenant }
        default:
          return {}
      }
    },
    async load_all() {
      this.page_loading = true
      // the saved config carries the user's last range, and both data loads
      // below are windowed by it, so this one waits rather than running
      // alongside them -- otherwise every load fetches the default window
      // first and then immediately refetches the saved one.
      await this.load_layout()
      await Promise.all([this.load_summary(), this.load_alerts_detail()])
      this.page_loading = false
    },
    async load_summary() {
      this.stats_loading = true
      try {
        const { value: { data = {} } } = await get_tenant_dashboard(
          this.user.tenant, moment().subtract(1, 'days').format('YYYY-MM-DD'), this.days_range
        )
        this.summary = data
      } catch (e) {
        this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
      }
      this.stats_loading = false
    },
    // one shared get_alerts call for every alert-detail widget (Alerts
    // Summary, Alerts By Type, Delivery Status, Recent Alerts, Recent
    // Delivery Errors) instead of each self-fetching the same data.
    async load_alerts_detail() {
      if (!this.features.alerts_enabled) {
        this.alerts_detail = []
        this.alerts_truncated = false
        this.alerts_loading = false
        return
      }
      this.alerts_loading = true
      try {
        const window_start = this.range_start_date()
        const { value: { data = {} } } = await get_alerts(
          this.user.tenant, '',
          window_start.format('YYYY-MM-DD'),
          moment().format('YYYY-MM-DD'),
          'widgets'
        )
        // the api filters on its own stored date field, so records whose
        // processed_time lands in the local day before window_start come back
        // too -- enough to put a 4th and 5th bar under a "3d" badge on the
        // by-day charts. every alert widget reads processed_time, so the
        // shared list is trimmed on it once here rather than per widget.
        this.alerts_detail = (data.alerts || []).filter(
          alert => !alert.processed_time || moment(alert.processed_time).isSameOrAfter(window_start)
        )
        this.alerts_truncated = !!(data.summary && data.summary.truncated)
        this.alerts_results_limit = (data.summary && data.summary.results_limit) || 0
      } catch (e) {
        this.alerts_detail = []
        this.alerts_truncated = false
      }
      this.alerts_loading = false
    },
    // the first day of the selected window, inclusive: "3d" is today plus the
    // two days before it, not today minus three. every date-scoped widget
    // measures from here so the badge and the bars agree.
    range_start_date() {
      return moment().startOf('day').subtract(this.days_range - 1, 'days')
    },
    set_days_range(option) {
      if (option === this.days_range) return
      this.days_range = option
      // remembered on the user's profile so the next visit (or another
      // device) opens on the window they last picked. a failed write just
      // leaves the previous choice saved and retries on the next click --
      // same philosophy as the nav favorites writes.
      update_dashboard_range(option).catch(() => {})
      this.load_summary()
      this.load_alerts_detail()
    },
    async load_layout() {
      let saved = null
      try {
        const { value: { data = {} } } = await get_dashboard_config()
        saved = data.dashboard_widgets
        // the api only hands back a range it still recognizes, but a client
        // that dropped an option since it was saved checks too
        if (DATE_RANGE_OPTIONS.includes(data.dashboard_days_range)) {
          this.days_range = data.dashboard_days_range
        }
      } catch (e) {
        saved = null
      }
      const applicableIds = new Set(this.catalog.map(widget => widget.id))
      const cleaned = (saved || []).filter(entry => applicableIds.has(entry.id))
      // a saved layout still wins for every widget it mentions -- the merge
      // only folds in widgets added to the catalog since it was saved, which
      // otherwise reach existing users only if they go looking in Add Widget.
      // not persisted here: it re-merges each load until the user next saves.
      this.layout = cleaned.length ? mergeNewDefaults(cleaned, this.user) : getDefaultLayout(this.user)
    },
    start_editing() {
      this.editing_snapshot = JSON.parse(JSON.stringify(this.layout))
      this.editing = true
    },
    cancel_editing() {
      this.layout = this.editing_snapshot || this.layout
      this.editing = false
      this.editing_snapshot = null
    },
    async save_editing() {
      this.saving = true
      try {
        await update_dashboard_config(this.layout)
      } catch (e) {
        this.$emit('showAlert', 'error saving your dashboard layout, please try again', 'danger', 5000)
      }
      this.saving = false
      this.editing = false
      this.editing_snapshot = null
    },
    reset_to_defaults() {
      this.layout = getDefaultLayout(this.user)
    },
    toggle_visible(id) {
      const entry = this.layout.find(w => w.id === id)
      if (entry) entry.visible = !entry.visible
    },
    add_widget(id) {
      if (!this.layout.some(w => w.id === id)) this.layout.push({ id, visible: true })
      this.show_add_modal = false
    },
  },
  watch: {
    user: {
      deep: true,
      handler() {
        this.load_all()
      },
    },
  },
}
</script>

<style scoped>
/* the page gutter belongs to whichever shell is wrapping us, and only one
   of them has a shell. horizon/midnight render inside .fi-hz-content, which
   already pads 16px/18px (12px/10px on a phone) -- there this page was
   adding another 20px on top, so a 375px screen spent 30px a side before
   the card's own padding started. classic has no such wrapper at all
   (body > .container-fluid > .mdash-page, every one of them unpadded), so
   there the gutter has to come from here or the cards sit flush against the
   window edge. these values mirror what the horizon shell provides, so both
   themes read with the same margins. */
.mdash-page {
  margin: 16px 18px 24px;
}

.fi-hz-content .mdash-page {
  margin: 0 0 24px;
}

/* page header rail. same construction as the ops_desk / support-ticket rail
   (3px accent edge, gradient band, title left / actions right) so the
   landing surfaces read as one family. built entirely on tokens, so unlike
   .od-rail -- whose #1d1d1f title needs a midnight.css override -- this one
   themes itself across all three skins with no counterpart rule.
   the 19px left padding is deliberate: 3px accent + 19px puts the title on
   x=42, which is exactly where the card headers below start their own
   titles (2px card border + 20px header padding). */
.mdash-rail {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  background: var(--fi-ops-rail-bg);
  border-left: 3px solid var(--fi-rail-accent);
  border-radius: 0 var(--fi-radius-md) var(--fi-radius-md) 0;
  padding: 12px 16px 12px 19px;
  margin-bottom: 14px;
}

/* flex, not just a heading: App.vue's .container-fluid sets text-align:center
   and every landing page inherits it, so a plain h3 here renders centred in
   the band. a flex container lays its content from the start edge regardless,
   which is also how .od-rail-title stays left-aligned. */
.mdash-rail-title {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  padding: 0;
  font-size: 22px;
  line-height: 1.2;
  font-weight: 700;
  letter-spacing: -0.1px;
  color: var(--fi-text);
}

/* shrink-0 so the range group never gets squeezed; the rail wraps instead */
.mdash-rail-actions {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

/* icon only: the sliders glyph is the same one the edit bar's controls sit
   under, and dropping the word keeps the rail on one line at phone widths.
   the label lives on title/aria-label rather than in the button. */
.mdash-customize-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 31px;
  padding-left: 0;
  padding-right: 0;
}

.mdash-range-group {
  display: inline-flex;
  border: 1px solid var(--fi-line-strong);
  border-radius: var(--fi-radius-sm);
  overflow: hidden;
}

.mdash-range-btn {
  background: var(--fi-surface);
  color: var(--fi-text-muted);
  border: none;
  border-left: 1px solid var(--fi-line-strong);
  padding: 5px 12px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
}

.mdash-range-btn:first-child {
  border-left: none;
}

.mdash-range-btn:hover:not(:disabled) {
  background: var(--fi-surface-sunken);
  color: var(--fi-text);
}

.mdash-range-btn:disabled {
  cursor: default;
  opacity: 0.6;
}

.mdash-range-btn--active {
  background: var(--fi-brand);
  color: var(--fi-brand-contrast);
}

.mdash-range-btn--active:hover:not(:disabled) {
  background: var(--fi-brand);
  color: var(--fi-brand-contrast);
}

.mdash-edit-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  background: var(--fi-brand-tint);
  border: 1px solid var(--fi-brand-tint-border);
  border-radius: var(--fi-radius-md);
  padding: 10px 14px;
  margin-bottom: 16px;
}

.mdash-edit-bar-hint {
  font-size: 12.5px;
  color: var(--fi-brand-strong);
}

.mdash-edit-bar-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

/* masonry-style columns: widgets pack upward so a short card leaves no
   dead space below it before the next widget. column-gap only affects the
   horizontal gutter between columns in CSS multicol -- there's no row-gap
   equivalent for stacked items, so the slight vertical spacing between
   cards comes from margin-bottom on .mdash-card instead. */
.mdash-grid {
  column-count: 2;
  column-gap: 14px;
}

/* mirrors site_map.vue's .sm-section-card banding: a top + left accent bar
   (::before/::after) framing a soft-tinted header and body, rather than a
   single flat left rail -- so a dashboard card reads as the same visual
   system as a site map section card, just with --mdash-accent (the widget's
   category color) standing in for --sm-section-accent. */
.mdash-card {
  display: inline-block;
  width: 100%;
  vertical-align: top;
  break-inside: avoid;
  page-break-inside: avoid;
  -webkit-column-break-inside: avoid;
  margin-bottom: 14px;
  background: var(--fi-surface);
  border: 2px solid color-mix(in srgb, var(--mdash-accent, var(--fi-rail-accent)) 28%, var(--fi-line));
  border-radius: 16px;
  box-shadow:
    0 4px 22px color-mix(in srgb, var(--mdash-accent, var(--fi-rail-accent)) 14%, transparent),
    0 1px 4px rgba(20, 40, 60, 0.06);
  position: relative;
  overflow: hidden;
}

.mdash-card::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 6px;
  background: linear-gradient(
    90deg,
    var(--mdash-accent, var(--fi-rail-accent)),
    color-mix(in srgb, var(--mdash-accent, var(--fi-rail-accent)) 55%, white)
  );
  z-index: 3;
}

.mdash-card::after {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  background: linear-gradient(
    180deg,
    var(--mdash-accent, var(--fi-rail-accent)),
    color-mix(in srgb, var(--mdash-accent, var(--fi-rail-accent)) 70%, white)
  );
  z-index: 1;
}

.mdash-card--full {
  column-span: all;
}

@media (max-width: 860px) {
  .mdash-grid {
    column-count: 1;
  }
}


.mdash-card--hidden {
  opacity: 0.55;
}

.mdash-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 14px 16px 12px 20px;
  background: color-mix(in srgb, var(--mdash-accent, var(--fi-rail-accent)) 14%, var(--fi-surface));
  border-bottom: 2px solid color-mix(in srgb, var(--mdash-accent, var(--fi-rail-accent)) 22%, var(--fi-line));
  position: relative;
  z-index: 2;
}

.mdash-card-header--draggable {
  cursor: grab;
}

.mdash-card-header--draggable:active {
  cursor: grabbing;
}

.mdash-card-body {
  padding: 14px 16px 16px 20px;
  background: color-mix(in srgb, var(--mdash-accent, var(--fi-rail-accent)) 6%, var(--fi-surface));
  position: relative;
  z-index: 2;
}

.mdash-card-title {
  font-size: 15px;
  font-weight: 700;
  color: color-mix(in srgb, var(--mdash-accent, var(--fi-rail-accent)) 65%, var(--fi-text));
  display: flex;
  align-items: center;
  gap: 8px;
  /* absorb the leftover header width so the range badge and See more link
     sit together against the right edge instead of being spread out by the
     header's space-between */
  flex: 1 1 auto;
  min-width: 0;
}

/* see-more: compact accent pill. sibling geometry with .mdash-range-badge:
   same radius, same rest fill; stronger ink + icon + hover mark it as the
   interactive one. 22px tall (12px text + 8px pad + 2px border) so it never
   outgrows the 15px title line. */
.mdash-see-more {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  position: relative;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  flex-shrink: 0;
  padding: 4px 7px 4px 10px;
  border-radius: var(--fi-radius-pill);
  /* !important: App.vue's global a:where(:not(.btn)) forces --fi-link ink
     with !important, same trap fi_fav_star documents */
  color: color-mix(in srgb, var(--mdash-accent, var(--fi-rail-accent)) 55%, var(--fi-text)) !important;
  background: color-mix(in srgb, var(--mdash-accent, var(--fi-rail-accent)) 6%, var(--fi-surface));
  border: 1px solid color-mix(in srgb, var(--mdash-accent, var(--fi-rail-accent)) 30%, var(--fi-line));
  text-decoration: none;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

/* touch: stretch the hit area to ~34px tall without growing the visual pill */
.mdash-see-more::after {
  content: '';
  position: absolute;
  left: -4px;
  right: -4px;
  top: -6px;
  bottom: -6px;
}

.mdash-see-more .fa {
  font-size: 12px;
  line-height: 1;
  transition: transform 0.15s ease;
}

/* hover crosses the header band tint (14%) so the change reads in light,
   horizon, and midnight alike */
.mdash-see-more:hover {
  color: var(--fi-text) !important;
  background: color-mix(in srgb, var(--mdash-accent, var(--fi-rail-accent)) 20%, var(--fi-surface));
  border-color: color-mix(in srgb, var(--mdash-accent, var(--fi-rail-accent)) 48%, var(--fi-line));
  text-decoration: none;
}

.mdash-see-more:hover .fa {
  transform: translateX(2px);
}

.mdash-see-more:focus-visible {
  outline: 2px solid color-mix(in srgb, var(--mdash-accent, var(--fi-rail-accent)) 70%, var(--fi-text));
  outline-offset: 2px;
}

@media (prefers-reduced-motion: reduce) {
  .mdash-see-more,
  .mdash-see-more .fa {
    transition: none;
  }
}

.mdash-card-title .fa:not(.mdash-drag-handle) {
  color: var(--fi-brand);
}

.mdash-card-subtitle {
  font-size: 13px;
  font-weight: 500;
  color: var(--fi-text-muted);
}

.mdash-card-subtitle::before {
  content: '\2014';
  margin-right: 8px;
  color: var(--fi-line-strong);
}

/* days-range chip: passive sibling of the see-more pill. same radius and
   rest fill, quieter border and muted-leaning ink, no icon, no hover. */
.mdash-range-badge {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  padding: 3px 9px;
  border-radius: var(--fi-radius-pill);
  color: color-mix(in srgb, var(--mdash-accent, var(--fi-rail-accent)) 35%, var(--fi-text-muted));
  background: color-mix(in srgb, var(--mdash-accent, var(--fi-rail-accent)) 6%, var(--fi-surface));
  border: 1px solid color-mix(in srgb, var(--mdash-accent, var(--fi-rail-accent)) 20%, var(--fi-line));
  white-space: nowrap;
  flex-shrink: 0;
}

.mdash-drag-handle {
  cursor: grab;
  color: var(--fi-text-faint);
}

.mdash-icon-btn {
  background: none;
  border: none;
  color: var(--fi-text-muted);
  padding: 2px 6px;
  border-radius: var(--fi-radius-sm);
}

.mdash-icon-btn:hover {
  background: var(--fi-surface-sunken);
  color: var(--fi-text);
}

.mdash-ghost {
  opacity: 0.4;
}

.mdash-add-group {
  margin-bottom: 18px;
}

.mdash-add-group-title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--fi-text-muted);
  margin-bottom: 8px;
}

.mdash-add-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mdash-add-row {
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid var(--fi-line);
  border-radius: var(--fi-radius-md);
  padding: 10px 12px;
}

.mdash-add-row-icon {
  width: 34px;
  height: 34px;
  border-radius: var(--fi-radius-sm);
  background: var(--fi-brand-tint);
  color: var(--fi-brand);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.mdash-add-row-text {
  flex: 1;
  min-width: 0;
}

.mdash-add-row-title {
  font-size: 13.5px;
  font-weight: 700;
  color: var(--fi-text);
}

.mdash-add-row-desc {
  font-size: 12px;
  color: var(--fi-text-muted);
}

/* phones: with the page's own gutter gone, the card's chrome is the last
   place width is going, so pull that in too. left padding stays at 15px
   because the 6px ::after accent bar lives inside it and needs clearance.
   this block sits at the end of the stylesheet on purpose -- a media query
   adds no specificity, so declared any earlier the base .mdash-card-body
   padding below it would simply win. */
@media (max-width: 600px) {
  /* classic's own gutter shrinks to match what the horizon shell drops to */
  .mdash-page {
    margin: 12px 10px 16px;
  }

  .fi-hz-content .mdash-page {
    margin: 0 0 16px;
  }

  .mdash-card {
    margin-bottom: 10px;
    border-radius: 12px;
  }

  .mdash-card-header {
    padding: 11px 12px 10px 15px;
  }

  .mdash-card-body {
    padding: 12px 12px 13px 15px;
  }

  /* one line, always: the rail wraps by default, and with five range chips
     plus the customize button that put the title on its own row and cost a
     whole band of vertical space above the first card. nowrap instead, with
     the title shrinking (it is the only elastic thing here) and the chips
     tightened just enough that the group still fits a 320px viewport. */
  .mdash-rail {
    flex-wrap: nowrap;
    gap: 8px;
    padding: 10px 12px 10px 15px;
    margin-bottom: 10px;
  }

  .mdash-rail-title {
    font-size: 19px;
    min-width: 0;
  }

  /* the whole point of the nowrap above: the title gives up room first, and
     ellipsizes rather than pushing the chips onto a second line. the span is
     what shrinks -- the favorite star beside it is a flex item of its own and
     must stay whole. */
  .mdash-rail-title-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .mdash-rail-actions {
    gap: 8px;
  }

  .mdash-range-btn {
    padding: 5px 8px;
    font-size: 12px;
  }
}
</style>
